
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../AuthContext';
const { rootUrl } = require('../../../../config/config');

const toLocalDatetimeInput = (d) => {
    const date = d ? new Date(d) : new Date();
    const tzOffset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - tzOffset * 60 * 1000);
    return local.toISOString().slice(0, 16);
};

const AddTimelineNote = ({ open, onClose, plateNo, onSaved }) => {
    const [form, setForm] = useState({
        plate_no: plateNo || '',
        event_type: 'note',
        title: '',
        description: '',
        event_date: toLocalDatetimeInput(new Date()),
        created_by: ''
    });

    const [errors, setErrors] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { user } = useAuth();

    useEffect(() => {
        setForm(prev => ({
            ...prev,
            plate_no: plateNo || prev.plate_no,
        }));

        const storedUser = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage.getItem('username') : null;
        const createdByName = user?.name || storedUser;
        if (createdByName) {
            setForm(prev => ({ ...prev, created_by: createdByName }));
        } else {
            setForm(prev => ({ ...prev, created_by: prev.created_by || 'system' }));
        }

        // Reset errors when opening
        if (open) {
            setErrors({});
            setError('');
            setForm(prev => ({ ...prev, event_date: prev.event_date || toLocalDatetimeInput(new Date()) }));
        }
    }, [plateNo, open, user]);

    const timeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    // close on escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (onClose) onClose();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const validate = () => {
        const newErrors = {};
        if (!form.plate_no) newErrors.plate_no = 'Plate number is required';
        if (!form.event_type) newErrors.event_type = 'Event type is required';
        if (!form.title) newErrors.title = 'Title is required';
        if (!form.created_by) newErrors.created_by = 'Created by is required';
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setError('Please fix the highlighted errors before saving.');
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                plate_no: form.plate_no,
                event_date: form.event_date ? new Date(form.event_date).toISOString() : new Date().toISOString(),
                event_type: form.event_type,
                title: form.title,
                description: form.description,
                created_by: form.created_by
            };

            const endpoint = `${rootUrl.replace(/\/$/, '')}/cars/timeline`;
            const token = (typeof window !== 'undefined' && (localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('auth_token'))) || '';
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                const msg = err.error || err.message || 'Failed to save timeline note';
                setError(msg);
            } else {
                const data = await res.json().catch(() => ({}));
                const msg = data?.message || 'Timeline note saved successfully';
                setSuccess(msg);
                // show success for 1 second then call onSaved
                const t = setTimeout(() => {
                    if (onSaved) onSaved(data);
                }, 1000);
                // store timeout id to clear if component unmounts
                timeoutRef.current = t;
            }
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error saving timeline note');
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => { if (onClose) onClose(); }}
        >
            <div className="bg-white rounded-sm shadow-none border border-gray-200 w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b">
                    <h3 className="text-base font-semibold">Add Timeline Note for {plateNo}</h3>
                </div>
                <form onSubmit={handleSubmit} className="p-4">
                    <div className="grid grid-cols-1 gap-3">
                        {success && (
                            <div className="md:col-span-2 mb-3">
                                <div className="flex items-start justify-between bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded-none">
                                    <div className="flex-1 text-sm">
                                        <strong className="block">Success</strong>
                                        <div className="mt-1">{success}</div>
                                    </div>
                                    <button type="button" onClick={() => setSuccess('')} className="ml-4 text-green-600 hover:text-green-800">✕</button>
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="md:col-span-2 mb-3">
                                <div className="flex items-start justify-between bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-none">
                                    <div className="flex-1 text-sm">
                                        <strong className="block">Error</strong>
                                        <div className="mt-1">{error}</div>
                                    </div>
                                    <button type="button" onClick={() => setError('')} className="ml-4 text-red-600 hover:text-red-800">✕</button>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium">Event Type</label>
                            <select name="event_type" value={form.event_type} onChange={handleChange} className="mt-1 block w-full border rounded-sm px-2 py-1.5 text-sm">
                                <option value="note">Note</option>
                                <option value="maintenance">Maintenance</option>
                                <option value="accident">Accident</option>
                                <option value="change">Change</option>
                                <option value="inspection_link">Inspection Link</option>
                                <option value="custom">Custom</option>
                            </select>
                            {errors.event_type && <div className="text-sm text-red-600">{errors.event_type}</div>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium">Title</label>
                            <input name="title" value={form.title} onChange={handleChange} className="mt-1 block w-full border rounded-sm px-2 py-1.5 text-sm" />
                            {errors.title && <div className="text-sm text-red-600">{errors.title}</div>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium">Description</label>
                            <textarea name="description" value={form.description} onChange={handleChange} className="mt-1 block w-full border rounded-sm px-2 py-1.5 text-sm h-28" />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-medium">Event Date</label>
                                <input name="event_date" type="datetime-local" value={form.event_date} onChange={handleChange} className="mt-1 block w-full border rounded-sm px-2 py-1.5 text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Created By</label>
                                <input name="created_by" value={form.created_by} onChange={handleChange} className="mt-1 block w-full border rounded-sm px-2 py-1.5 text-sm" readOnly={!!user?.name} />
                                {errors.created_by && <div className="text-sm text-red-600">{errors.created_by}</div>}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-2 pt-2">
                            <button type="button" disabled={submitting} onClick={onClose} className="px-4 py-2 bg-gray-100 text-sm rounded-sm">Cancel</button>
                            <button type="submit" disabled={submitting} className="px-4 py-2 bg-brand-primary text-white text-sm rounded-sm">{submitting ? 'Saving...' : 'Save'}</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddTimelineNote;
